import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FiBook, FiUsers, FiSettings, FiShield, FiZap,
  FiArrowRight, FiCheckCircle, FiBriefcase, FiLayers, FiGrid,
  FiSearch, FiFileText, FiHelpCircle
} from 'react-icons/fi';
import PublicPageHeader from '../../components/common/PublicPageHeader';
import PublicPageFooter from '../../components/common/PublicPageFooter';

const DocsHome = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  
  const quickLinks = [
    {
      title: 'Quick Start Guide',
      description: 'Get up and running with Tasklina in under 5 minutes',
      icon: FiZap,
      link: '/docs/getting-started',
      color: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600'
    },
    {
      title: 'Personal Workspace',
      description: 'Learn about individual task and project management',
      icon: FiUsers,
      link: '/docs/personal',
      color: 'bg-green-50 dark:bg-green-900/30 text-green-600'
    },
    {
      title: 'Organization Workspace',
      description: 'Enterprise features for teams and departments',
      icon: FiBriefcase,
      link: '/docs/organization',
      color: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600'
    },
    {
      title: 'Admin & Permissions',
      description: 'Role-based access control and admin features',
      icon: FiShield,
      link: '/docs/admin',
      color: 'bg-red-50 dark:bg-red-900/30 text-red-600'
    }
  ];

  const categories = [
    {
      title: 'Getting Started',
      icon: FiBook,
      description: 'Everything you need to begin',
      articles: [
        { title: 'Creating Your Account', link: '/docs/getting-started#account' },
        { title: 'First Project Setup', link: '/docs/getting-started#first-project' },
        { title: 'Inviting Team Members', link: '/docs/getting-started#invite' },
        { title: 'Understanding Workspaces', link: '/docs/getting-started#workspaces' }
      ]
    },
    {
      title: 'Personal Workspace',
      icon: FiUsers,
      description: 'Individual productivity features',
      articles: [
        { title: 'Task Management', link: '/docs/personal/tasks' },
        { title: 'Project Organization', link: '/docs/personal/projects' },
        { title: 'Personal Teams', link: '/docs/personal/teams' },
        { title: 'Personal Finance', link: '/docs/personal/finance' }
      ]
    },
    {
      title: 'Organization Workspace',
      icon: FiBriefcase,
      description: 'Enterprise team management',
      articles: [
        { title: 'Organization Setup', link: '/docs/organization#setup' },
        { title: 'Department Management', link: '/docs/organization/departments' },
        { title: 'Team Management', link: '/docs/organization/teams' },
        { title: 'Organization Finance', link: '/docs/organization/finance' }
      ]
    },
    {
      title: 'Admin & Security',
      icon: FiShield,
      description: 'Access control and compliance',
      articles: [
        { title: 'Role Hierarchy', link: '/docs/admin/roles' },
        { title: 'Permission Management', link: '/docs/admin/permissions' },
        { title: 'Organization Admin', link: '/docs/admin/org-admin' },
        { title: 'Security Settings', link: '/docs/admin/security' }
      ]
    },
    {
      title: 'Features',
      icon: FiLayers,
      description: 'In-depth feature guides',
      articles: [
        { title: 'Views & Boards', link: '/docs/features/views' },
        { title: 'Notifications', link: '/docs/features/notifications' },
        { title: 'Calendar & Scheduling', link: '/docs/features/calendar' },
        { title: 'Analytics & Reports', link: '/docs/features/analytics' },
        { title: 'Timeline / Gantt View', link: '/docs/features/timeline' },
        { title: 'Task Dependencies', link: '/docs/features/dependencies' }
      ]
    }
  ];

  const popularArticles = [
    { title: 'Understanding Personal vs Organization Workspaces', link: '/docs/getting-started#workspaces' },
    { title: 'Setting Up Your Organization', link: '/docs/organization#setup' },
    { title: 'Role-Based Access Control (RBAC)', link: '/docs/admin/roles' },
    { title: 'Managing Department Budgets', link: '/docs/organization/finance' },
    { title: 'Creating and Managing Projects', link: '/docs/personal/projects' },
    { title: 'Inviting and Managing Team Members', link: '/docs/organization/teams' }
  ];

  // Flatten all articles for search
  const allArticles = useMemo(() => {
    const articles = [];
    // Add quick links
    quickLinks.forEach(item => {
      articles.push({ title: item.title, link: item.link, description: item.description, category: 'Quick Links' });
    });
    // Add category articles
    categories.forEach(category => {
      category.articles.forEach(article => {
        articles.push({ title: article.title, link: article.link, category: category.title });
      });
    });
    // Add popular articles
    popularArticles.forEach(article => {
      articles.push({ title: article.title, link: article.link, category: 'Popular' });
    });
    return articles;
  }, []);

  // Filter articles based on search query
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return allArticles.filter(article => 
      article.title.toLowerCase().includes(query) ||
      (article.description && article.description.toLowerCase().includes(query)) ||
      article.category.toLowerCase().includes(query)
    ).slice(0, 8); // Limit to 8 results
  }, [searchQuery, allArticles]);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchResults.length > 0) {
      navigate(searchResults[0].link);
      setSearchQuery('');
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white dark:from-gray-800 dark:to-gray-900 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex p-3 bg-blue-100 dark:bg-blue-900/30 dark:bg-blue-900/30 rounded-2xl mb-6">
              <FiBook className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              Tasklina Documentation
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed mb-8">
              Comprehensive guides and documentation to help you get the most out of Tasklina, 
              whether you're an individual user or managing an enterprise team.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search documentation..."
                className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm"
              />
              
              {/* Search Results Dropdown */}
              {searchQuery && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden z-50">
                  {searchResults.map((result, index) => (
                    <Link
                      key={index}
                      to={result.link}
                      onClick={() => setSearchQuery('')}
                      className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                    >
                      <div className="flex items-center space-x-3">
                        <FiFileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{result.title}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{result.category}</p>
                        </div>
                      </div>
                      <FiArrowRight className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    </Link>
                  ))}
                </div>
              )}
              
              {/* No Results Message */}
              {searchQuery && searchResults.length === 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg p-4 z-50">
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center">No matching articles found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 border-b border-gray-100 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickLinks.map((item, index) => {
              const Icon = item.icon;
              return (
                <Link
                  key={index}
                  to={item.link}
                  className="group bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm hover:shadow-md hover:border-blue-200 dark:hover:border-blue-700 transition-all"
                >
                  <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {item.description}
                  </p>
                  <div className="flex items-center mt-4 text-blue-600 dark:text-blue-400 text-sm font-medium">
                    <span>Learn more</span>
                    <FiArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Documentation Categories */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Browse Documentation</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => {
              const Icon = category.icon;
              return (
                <div
                  key={index}
                  className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 shadow-sm"
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 dark:bg-gray-700 rounded-xl flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gray-700 dark:text-gray-200 dark:text-gray-300" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">{category.title}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{category.description}</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {category.articles.map((article, idx) => (
                      <li key={idx}>
                        <Link
                          to={article.link}
                          className="flex items-center text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm py-1"
                        >
                          <FiFileText className="w-4 h-4 mr-2 flex-shrink-0" />
                          <span>{article.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Popular Articles */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Popular Articles</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {popularArticles.map((article, index) => (
              <Link
                key={index}
                to={article.link}
                className="flex items-center space-x-3 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-700 p-4 hover:shadow-md hover:border-blue-200 dark:hover:border-blue-700 transition-all"
              >
                <FiCheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0" />
                <span className="text-gray-700 dark:text-gray-200 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  {article.title}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Need Help Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl p-8 md:p-12 text-white">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="mb-6 md:mb-0">
                <div className="flex items-center space-x-2 mb-2">
                  <FiHelpCircle className="w-6 h-6" />
                  <span className="text-blue-200 font-medium">Need more help?</span>
                </div>
                <h3 className="text-2xl font-bold mb-2">Can't find what you're looking for?</h3>
                <p className="text-blue-100">
                  Our support team is ready to assist you with any questions.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/help"
                  className="px-6 py-3 bg-white dark:bg-gray-800 text-blue-600 rounded-xl font-medium hover:bg-blue-50 dark:bg-blue-900/30 transition-colors text-center"
                >
                  Visit Help Center
                </Link>
                <Link
                  to="/contact"
                  className="px-6 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-400 transition-colors text-center"
                >
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PublicPageFooter />
    </div>
  );
};

export default DocsHome;
